import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import axios from 'axios';
import { Plane, Calendar as CalendarIcon, Mail, MessageSquare, TrendingUp, Eye, Heart, BarChart3, Clock, Globe, Filter } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend 
} from 'recharts';
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  '#002d5b', // Navy
  '#ef4444', // Red
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Orange
];

// Formateador de segundos a mm:ss
const formatTime = (seconds) => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [visitMetrics, setVisitMetrics] = useState(null);
  const [range, setRange] = useState('month');
  const [env, setEnv] = useState('REAL');
  const [loading, setLoading] = useState(true);
  const [customRange, setCustomRange] = useState({ from: null, to: null });

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        let url = `${API}/admin/metrics/visits?range=${range}&env=${env}`;
        if (range === 'custom' && customRange.from && customRange.to) {
          url += `&start=${customRange.from.toISOString()}&end=${customRange.to.toISOString()}`;
        }

        const [statsRes, mRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(url, { headers })
        ]);
        setStats(statsRes.data);
        setVisitMetrics(mRes.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [range, env, customRange]);

  const handleCustomRange = (rangeData) => {
    if (rangeData?.from && rangeData?.to) {
      setCustomRange(rangeData);
      setRange('custom');
    }
  };

  if (loading || !stats || !visitMetrics) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const STAT_CARDS = [
    { icon: Plane, label: 'Viajes activos', value: stats.summary.trips, color: 'bg-blue-500' },
    { icon: CalendarIcon, label: 'Reservas totales', value: stats.summary.bookings, color: 'bg-green-500' },
    { icon: Mail, label: 'Suscriptores', value: stats.summary.subscribers, color: 'bg-purple-500' },
    { icon: MessageSquare, label: 'Mensajes contacto', value: stats.summary.messages, color: 'bg-orange-500' },
  ];

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-primary">Dashboard Analytics</h1>
          <p className="text-muted-foreground">Métricas de rendimiento de Viajes de Ensueño</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Última actualización</p>
          <p className="font-medium">{new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {STAT_CARDS.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-primary">{stat.value}</p>
            <p className="text-muted-foreground text-sm uppercase tracking-wider font-medium">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Visits Metrics Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 mb-8">
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-8 gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-primary">Tráfico del Sitio</h2>
              <div className="flex items-center gap-4 mt-1">
                <p className="text-sm text-muted-foreground">Visitas: <span className="font-bold text-blue-600">{visitMetrics.summary.total_period}</span></p>
                <div className="w-1 h-1 bg-slate-200 rounded-full" />
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Tiempo medio: <span className="font-bold text-slate-700">{formatTime(visitMetrics.summary.avg_session_time_seconds)}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 w-full xl:w-auto">
            {/* Entorno Selector */}
            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
              <Globe className="w-4 h-4 ml-2 text-slate-400" />
              {[
                { id: 'REAL', label: 'Real' },
                { id: 'OCU', label: 'OCU' },
                { id: 'DEV', label: 'Dev' }
              ].map((e) => (
                <button
                  key={e.id}
                  onClick={() => setEnv(e.id)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${
                    env === e.id ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {e.label}
                </button>
              ))}
            </div>

            {/* Rango Selector */}
            <div className="flex bg-slate-100 p-1 rounded-lg">
              {[
                { id: 'day', label: 'Hoy' },
                { id: 'week', label: '7d' },
                { id: 'month', label: '30d' },
                { id: 'year', label: 'Año' }
              ].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRange(r.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    range === r.id ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {r.label}
                </button>
              ))}

              <div className="w-px h-4 bg-slate-200 mx-1 self-center" />

              <Popover>
                <PopoverTrigger asChild>
                  <button className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    range === 'custom' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {customRange.from && customRange.to ? (
                      `${format(customRange.from, 'dd/MM')} - ${format(customRange.to, 'dd/MM')}`
                    ) : (
                      'Personalizado'
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={customRange.from || new Date()}
                    selected={customRange}
                    onSelect={handleCustomRange}
                    numberOfMonths={2}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chart */}
          <div className="lg:col-span-2 h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={visitMetrics.over_time}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{fontSize: 10}} />
                <YAxis tick={{fontSize: 10}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Visitas" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="bg-slate-50 rounded-xl p-4 overflow-hidden border border-slate-100">
            <h3 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Páginas Populares y Tiempo
            </h3>
            <div className="space-y-1 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {visitMetrics.per_page.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0 pr-4">
                    <p className="text-xs font-medium text-slate-700 truncate" title={item.path}>{item.path || '/'}</p>
                    <p className="text-[10px] text-slate-400 font-mono">Tiempo medio: {formatTime(item.avg_duration)}</p>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-bold text-primary">{item.count}</span>
                    <span className="text-[9px] text-slate-400">vistas</span>
                  </div>
                </div>
              ))}
              {visitMetrics.per_page.length === 0 && (
                <p className="text-center text-slate-400 py-10">Sin datos de tráfico aún</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Most Viewed Trips */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <Eye className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-primary">Viajes más vistos</h2>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.most_viewed} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" hide />
                <YAxis dataKey="title" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="views" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Clicks by Category */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-primary">Interés por Categoría</h2>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.views_by_category}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="views"
                  nameKey="category"
                >
                  {stats.views_by_category.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout="vertical" align="right" verticalAlign="middle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Most Interacted Trips */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <MessageSquare className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-primary">Viajes con más Consultas</h2>
          </div>
          <div className="space-y-4">
            {stats.most_interacted.length > 0 ? stats.most_interacted.map((trip, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="font-medium text-slate-700">{trip.title}</span>
                <span className="bg-orange-100 text-orange-700 font-bold px-3 py-1 rounded-full text-xs">
                  {trip.count} mensajes
                </span>
              </div>
            )) : <p className="text-center text-muted-foreground py-10">Sin interacciones registradas</p>}
          </div>
        </div>

        {/* Most Reserved Trips */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <Heart className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-primary">Viajes más Reservados</h2>
          </div>
          <div className="space-y-4">
            {stats.most_reserved.length > 0 ? stats.most_reserved.map((trip, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="font-medium text-slate-700">{trip.title}</span>
                <span className="bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full text-xs">
                  {trip.count} reservas
                </span>
              </div>
            )) : <p className="text-center text-muted-foreground py-10">Sin reservas confirmadas</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
